/**
 * Adaptation coaching — which coaching action a talent receives.
 *
 * The server decides it: a company coaches one talent, a group (the inactive,
 * the streaks at risk, the top performers, the active learners) or a whole
 * engagement band, and the targets and each one's action are chosen over the
 * whole roster. This rule used to live in the Adaptation page, which picked
 * the action per row it happened to hold.
 */
import type { TFunction } from 'i18next';

import type { AdaptationEngagementLevel } from './engagement';

/** The five coaching actions — each is one message template a company can edit. */
export const COACHING_ACTIONS = [
  'encourage',
  'challenge',
  'checkin',
  'celebrate',
  'remind',
] as const;
export type CoachingAction = (typeof COACHING_ACTIONS)[number];

/**
 * The i18n key of each action's name — the one map every surface names a
 * coaching action through (`t(COACHING_ACTION_LABEL_KEY[action])`), guarded
 * against en.json in dynamic-key-coverage.spec.ts.
 */
export const COACHING_ACTION_LABEL_KEY = {
  encourage: 'business.adaptationPage.coachActions.encourage',
  challenge: 'business.adaptationPage.coachActions.challenge',
  checkin: 'business.adaptationPage.coachActions.checkin',
  celebrate: 'business.adaptationPage.coachActions.celebrate',
  remind: 'business.adaptationPage.coachActions.remind',
} as const satisfies Record<CoachingAction, string>;

/**
 * Each engagement band's default coaching approach:
 *  - inactive → check in (re-engage, lighter touch than coaching)
 *  - at_risk  → encourage (remove blockers, lean positive)
 *  - slowing  → encourage (motivate before it slips further)
 *  - active   → challenge (keep the momentum going)
 *  - thriving → celebrate (recognise and reinforce)
 */
export const BAND_COACHING_ACTION: Record<AdaptationEngagementLevel, CoachingAction> = {
  inactive: 'checkin',
  at_risk: 'encourage',
  slowing: 'encourage',
  active: 'challenge',
  thriving: 'celebrate',
};

/**
 * The action that fits the talent's own state: an inactive talent is checked
 * in on (a long-broken streak is moot), a streak at risk is reminded before it
 * breaks, anyone else gets their band's default.
 */
export function pickCoachingAction(talent: {
  engagementLevel: AdaptationEngagementLevel;
  streakAtRisk: boolean;
}): CoachingAction {
  if (talent.engagementLevel === 'inactive') return BAND_COACHING_ACTION.inactive;
  if (talent.streakAtRisk) return 'remind';
  return BAND_COACHING_ACTION[talent.engagementLevel];
}

/**
 * The coaching action an interaction is the record of, when its title is that
 * action's code — `null` when the title is someone's own words.
 *
 * An Adaptation coaching record (D1) is stored with `coaching.<action>` for a
 * title and its action beside it (`coachingAction`), so every reader names it by
 * the action in their own language — the company's lists, the citizen's Care
 * card, Inbox and timeline alike — never by the code. Once someone edits the
 * title, their text is the title.
 */
export function coachingRecordAction(row: {
  title: string;
  coachingAction: CoachingAction | null;
}): CoachingAction | null {
  return row.coachingAction && row.title === `coaching.${row.coachingAction}`
    ? row.coachingAction
    : null;
}

/** An interaction's title as every surface draws it (see {@link coachingRecordAction}). */
export function interactionTitle(
  row: { title: string; coachingAction: CoachingAction | null },
  t: TFunction
): string {
  const action = coachingRecordAction(row);
  return action ? t(COACHING_ACTION_LABEL_KEY[action]) : row.title;
}
